import type { ExerciseTemplate } from '@yuan-math/exercises';
import { choiceExercise, expr, expressionExercise, numericExercise, options, signedConstant, template, trap } from './authoring.ts';

/**
 * The three concepts that open Algebra I: rearranging formulas, domain and
 * range, and inequalities in two variables.
 *
 * Two authoring notes worth stating once. The formulas here use single letters
 * throughout, because the grader's expression parser reads each letter as its
 * own variable — `mass` would be m × a × s × s, which is a different and much
 * funnier quantity. And every distractor that is merely *plausible* has been
 * replaced by one that is *produced*: the wrong side of a boundary, the letter
 * the question did not ask for, a domain with no exclusions.
 */

/** Formulas whose letters are all single characters, with a target to isolate. */
const PRODUCT_FORMULAS = [
  { latex: 'A = bh', subject: 'A', factors: ['b', 'h'], meaning: 'area of a parallelogram' },
  { latex: 'd = st', subject: 'd', factors: ['s', 't'], meaning: 'distance travelled' },
  { latex: 'F = ma', subject: 'F', factors: ['m', 'a'], meaning: 'force' },
  { latex: 'V = wh', subject: 'V', factors: ['w', 'h'], meaning: 'volume of a unit-length box' },
  { latex: 'C = np', subject: 'C', factors: ['n', 'p'], meaning: 'cost of n items' },
  { latex: 'W = Fd', subject: 'W', factors: ['F', 'd'], meaning: 'work done' },
] as const;

/** Formulas of the shape subject = m·x + b, for the two-step rearrangements. */
const LINEAR_FORMULAS = [
  { subject: 'y', coefficient: 'm', variable: 'x', constant: 'b', meaning: 'a straight line' },
  { subject: 'C', coefficient: 'r', variable: 'h', constant: 'f', meaning: 'a call-out charge plus an hourly rate' },
  { subject: 'T', coefficient: 'p', variable: 'n', constant: 'd', meaning: 'a total with a fixed deposit' },
  { subject: 'S', coefficient: 'k', variable: 't', constant: 'c', meaning: 'a savings balance' },
] as const;

export const ALGEBRA_FOUNDATION_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------ literal equations */
  template({
    id: 'literal-solve-product',
    conceptId: 'literal-equations',
    description: 'Solve a product formula for one of its factors.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const formula = rng.pick(PRODUCT_FORMULAS);
      const wanted = rng.pick(formula.factors);
      const other = formula.factors.find((letter) => letter !== wanted) as string;

      return expressionExercise({
        prompt: `This is the formula for ${formula.meaning}. Solve it for ${wanted}.`,
        latex: formula.latex,
        canonical: `${formula.subject}/${other}`,
        summary: `${other} multiplies ${wanted}, so divide both sides by ${other}: ${wanted} = ${formula.subject}/${other}.`,
        hint: `What is ${other} doing to ${wanted}? Undo exactly that, on both sides.`,
        steps: [
          { label: `Divide both sides by ${other}.`, latex: `\\frac{${formula.subject}}{${other}} = ${wanted}` },
          { label: 'Write the wanted letter on the left.', latex: `${wanted} = \\frac{${formula.subject}}{${other}}` },
        ],
        traps: [
          // Moving a multiplying letter across by multiplying again.
          trap(`${formula.subject}*${other}`, 'literal-operation-inverted'),
          // Right rearrangement, wrong letter left standing alone.
          trap(`${formula.subject}/${wanted}`, 'wrong-letter-isolated'),
        ],
      });
    },
  }),

  template({
    id: 'literal-solve-linear',
    conceptId: 'literal-equations',
    description: 'Solve a two-step formula, where the subtraction must happen before the division.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const formula = rng.pick(LINEAR_FORMULAS);
      const { subject, coefficient, variable, constant } = formula;

      return expressionExercise({
        prompt: `Solve this formula for ${variable}.`,
        latex: `${subject} = ${coefficient}${variable} + ${constant}`,
        canonical: `(${subject} - ${constant})/${coefficient}`,
        summary: `Subtract ${constant} from both sides first, then divide the whole side by ${coefficient}: ${variable} = (${subject} − ${constant})/${coefficient}.`,
        hint: `${coefficient}${variable} is the term you want. What is being added to it, and what is multiplying it?`,
        steps: [
          { label: `Subtract ${constant} from both sides.`, latex: `${subject} - ${constant} = ${coefficient}${variable}` },
          { label: `Divide the whole side by ${coefficient}.`, latex: `\\frac{${subject} - ${constant}}{${coefficient}} = ${variable}` },
        ],
        traps: [
          // Dividing only the term next to the sign, losing the bracket.
          trap(`${subject} - ${constant}/${coefficient}`, 'literal-partial-division'),
          // Dividing before subtracting, so the constant never gets divided.
          trap(`${subject}/${coefficient} - ${constant}`, 'undo-out-of-order'),
        ],
      });
    },
  }),

  template({
    id: 'literal-rearrangement-choice',
    conceptId: 'literal-equations',
    description: 'Pick the correct rearrangement of a formula from four candidates.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const formula = rng.pick(LINEAR_FORMULAS);
      const { subject, coefficient, variable, constant } = formula;

      const { choices, correctId } = options(
        [
          { latex: `${variable} = \\frac{${subject} - ${constant}}{${coefficient}}`, correct: true },
          { latex: `${variable} = ${subject} - \\frac{${constant}}{${coefficient}}`, misconceptionId: 'literal-partial-division' },
          { latex: `${variable} = \\frac{${subject}}{${coefficient}} - ${constant}`, misconceptionId: 'undo-out-of-order' },
          { latex: `${variable} = ${coefficient}(${subject} - ${constant})`, misconceptionId: 'literal-operation-inverted' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Which of these solves the formula for ${variable}?`,
        latex: `${subject} = ${coefficient}${variable} + ${constant}`,
        choices,
        correctId,
        summary: `Undo the addition first, then divide the entire side: ${variable} = (${subject} − ${constant})/${coefficient}.`,
        hint: 'Two of these divide only part of a side. Which one divides all of it?',
      });
    },
  }),

  template({
    id: 'literal-substitute-after-rearranging',
    conceptId: 'literal-equations',
    description: 'Rearrange a formula and then use it, so the rearrangement has to be right.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      /*
       * Both the base and the height are even. The base keeps A = bh/2 whole,
       * and the height keeps the *trap* whole: a student who never undoes the
       * half answers A/b, which is h/2, and a trap that comes out fractional is
       * one no student would type and so one that never fires.
       */
      const b = 2 * rng.int(2, difficulty === 4 ? 12 : 6);
      const height = 2 * rng.int(1, difficulty === 4 ? 8 : 5);
      const A = (b * height) / 2;

      return numericExercise({
        prompt: 'A triangle has this area and this base. How tall is it?',
        latex: `A = \\tfrac{1}{2}bh, \\quad A = ${A}, \\quad b = ${b}`,
        value: height,
        summary: `Rearranged, h = 2A/b. That gives (2 × ${A}) ÷ ${b} = ${height}.`,
        hint: 'Solve the formula for h first, then substitute. The 2 does not stay on the bottom.',
        steps: [
          { label: 'Multiply both sides by 2.', latex: '2A = bh' },
          { label: 'Divide both sides by b.', latex: 'h = \\frac{2A}{b}' },
          { label: 'Substitute.', latex: `h = \\frac{${2 * A}}{${b}} = ${height}` },
        ],
        traps: [
          // The half never undone: A/b rather than 2A/b.
          trap(A / b, 'literal-operation-inverted'),
        ],
      });
    },
  }),

  /* -------------------------------------------------------- domain and range */
  template({
    id: 'domain-excluded-value',
    conceptId: 'domain-and-range',
    description: 'Find the input a rational rule has to exclude.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const excluded = rng.int(1, difficulty === 3 ? 12 : 7) * (rng.bool() ? 1 : -1);
      const numerator = rng.int(2, 9);

      return numericExercise({
        prompt: 'One value of x is not in the domain of this function. Which one?',
        latex: `f(x) = \\frac{${numerator}}{x ${signedConstant(-excluded)}}`,
        value: excluded,
        summary: `The denominator is zero when x = ${excluded}, and a fraction with zero on the bottom has no value. Every other input is fine.`,
        hint: 'A denominator may never be zero. Set the bottom equal to zero and solve.',
        steps: [
          { label: 'Set the denominator to zero.', latex: `x ${signedConstant(-excluded)} = 0` },
          { label: 'Solve.', latex: `x = ${excluded}` },
        ],
        traps: [
          // The sign read straight off the bracket.
          trap(-excluded, 'opposite-sign-unchanged'),
          // Excluding what makes the *numerator* zero, or nothing at all.
          trap(0, 'domain-restriction-ignored'),
        ],
      });
    },
  }),

  template({
    id: 'domain-of-radical',
    conceptId: 'domain-and-range',
    description: 'Find the smallest input a square root accepts.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const coefficient = difficulty === 4 ? rng.int(2, 5) : 1;
      const smallest = rng.int(1, 9) * (rng.bool() ? 1 : -1);
      const constant = -coefficient * smallest;

      return numericExercise({
        prompt: 'What is the smallest value of x this function accepts?',
        latex:
          coefficient === 1
            ? `g(x) = \\sqrt{x ${signedConstant(constant)}}`
            : `g(x) = \\sqrt{${coefficient}x ${signedConstant(constant)}}`,
        value: smallest,
        summary: `The inside may not be negative, so ${coefficient === 1 ? '' : `${coefficient}`}x ${signedConstant(constant)} ≥ 0, which gives x ≥ ${smallest}.`,
        hint: 'A square root cannot take a negative number. Set the inside greater than or equal to zero.',
        steps: [
          {
            label: 'The inside may not be negative.',
            latex: coefficient === 1 ? `x ${signedConstant(constant)} \\ge 0` : `${coefficient}x ${signedConstant(constant)} \\ge 0`,
          },
          { label: 'Solve the inequality.', latex: `x \\ge ${smallest}` },
        ],
        traps: [
          // The inequality solved as far as the x-term and then abandoned.
          ...(coefficient === 1 ? [] : [trap(-constant, 'coefficient-not-divided-out')]),
          // The sign of the constant read straight off.
          ...(-smallest === smallest ? [] : [trap(-smallest, 'opposite-sign-unchanged')]),
        ],
      });
    },
  }),

  template({
    id: 'range-of-parabola',
    conceptId: 'domain-and-range',
    description: 'Read the smallest or largest output of a quadratic from its vertex.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const h = rng.int(1, 8) * (rng.bool() ? 1 : -1);
      const k = rng.int(-9, 9);
      const opensUp = rng.bool();
      const sign = opensUp ? '' : '-';

      return numericExercise({
        prompt: `What is the ${opensUp ? 'smallest' : 'largest'} value this function can output?`,
        latex: expr(`y = ${sign}(x ${signedConstant(-h)})^2`, signedConstant(k)),
        value: k,
        summary: `The squared bracket is ${opensUp ? 'never negative, so the smallest it can be is 0' : 'never negative, so with the minus in front the largest it can be is 0'}. That happens at x = ${h}, where y = ${k}.`,
        hint: 'A squared bracket is never negative. What is the best it can do, and what is y there?',
        steps: [
          { label: 'The bracket is zero at its turning point.', latex: `x = ${h}` },
          { label: 'Substitute to find the output there.', latex: `y = ${k}` },
        ],
        traps: [
          // The x-coordinate of the vertex given where an output was asked for.
          ...(h === k ? [] : [trap(h, 'domain-and-range-swapped')]),
          // The sign inside the bracket read straight off.
          ...(-h === k ? [] : [trap(-h, 'vertex-sign-flipped')]),
        ],
      });
    },
  }),

  template({
    id: 'domain-range-identify',
    conceptId: 'domain-and-range',
    description: 'Choose the domain or range of a familiar function.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const k = rng.int(-9, 9);
      const askDomain = rng.bool();

      const entries = askDomain
        ? [
            { label: 'All real numbers', correct: true },
            { label: `x ≥ ${k}`, misconceptionId: 'domain-and-range-swapped' },
            { label: `x ≠ ${k}`, misconceptionId: 'domain-restriction-ignored' },
            { label: `x ≤ ${k}`, misconceptionId: 'domain-and-range-swapped' },
          ]
        : [
            { label: `y ≥ ${k}`, correct: true },
            { label: 'All real numbers', misconceptionId: 'parabola-range-unbounded' },
            { label: `x ≥ ${k}`, misconceptionId: 'domain-and-range-swapped' },
            { label: `y ≤ ${k}`, misconceptionId: 'vertex-sign-flipped' },
          ];
      const { choices, correctId } = options(entries, rng.shuffle);

      return choiceExercise({
        prompt: `What is the ${askDomain ? 'domain' : 'range'} of this function?`,
        latex: expr('y = x^2', signedConstant(k)),
        choices,
        correctId,
        summary: askDomain
          ? 'Any number at all can be squared, and nothing here divides or takes a root — so every real number is accepted.'
          : `x² is never negative, so the outputs start at ${k} and go up from there.`,
        hint: askDomain
          ? 'Is there any number you are forbidden to put in?'
          : 'What is the smallest x² can be, and what does y equal there?',
      });
    },
  }),

  /* ------------------------------------------- inequalities in two variables */
  template({
    id: 'inequality-two-var-test-point',
    conceptId: 'two-variable-inequalities',
    description: 'Pick the ordered pair that satisfies a linear inequality.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const slope = rng.int(1, difficulty === 3 ? 3 : 2) * (rng.bool() ? 1 : -1);
      const intercept = rng.int(-5, 5);
      const strict = rng.bool();
      const above = rng.bool();
      const sign = above ? (strict ? '>' : '\\ge') : strict ? '<' : '\\le';

      const x = rng.int(-3, 3);
      const onLine = slope * x + intercept;
      const gap = rng.int(2, 5);
      const solution = above ? onLine + gap : onLine - gap;
      const wrongSide = above ? onLine - gap : onLine + gap;
      // A second wrong-side point, so three of four options are not solutions.
      const farWrong = above ? onLine - gap - 3 : onLine + gap + 3;
      /*
       * The boundary point is offered only when the inequality is strict.
       * Under ≤ or ≥ the line belongs to the solution, so that option would be
       * a second correct answer rather than a diagnosis.
       */
      const third = strict
        ? { label: `(${x}, ${onLine})`, misconceptionId: 'test-point-on-boundary' }
        : { label: `(${x}, ${above ? onLine - 1 : onLine + 1})`, misconceptionId: 'half-plane-wrong-side' };

      const { choices, correctId } = options(
        [
          { label: `(${x}, ${solution})`, correct: true },
          { label: `(${x}, ${wrongSide})`, misconceptionId: 'half-plane-wrong-side' },
          third,
          { label: `(${x}, ${farWrong})`, misconceptionId: 'half-plane-wrong-side' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these points is a solution of the inequality?',
        latex: expr(`y ${sign} ${slope === 1 ? '' : slope === -1 ? '-' : slope}x`, signedConstant(intercept)),
        choices,
        correctId,
        summary: `At x = ${x} the boundary sits at y = ${onLine}, and the solutions are the points ${above ? 'above' : 'below'} it${strict ? ' — the line itself excluded' : ', the line included'}. Only (${x}, ${solution}) qualifies.`,
        hint: 'Put each pair into the inequality and see which one makes a true statement.',
      });
    },
  }),

  template({
    id: 'inequality-two-var-boundary',
    conceptId: 'two-variable-inequalities',
    description: 'Choose how the boundary is drawn and which side is shaded.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const slope = rng.int(1, 3) * (rng.bool() ? 1 : -1);
      const intercept = rng.int(-6, 6);
      const strict = rng.bool();
      const above = rng.bool();
      const sign = above ? (strict ? '>' : '\\ge') : strict ? '<' : '\\le';
      const style = strict ? 'Dashed' : 'Solid';
      const wrongStyle = strict ? 'Solid' : 'Dashed';
      const side = above ? 'above' : 'below';
      const wrongSide = above ? 'below' : 'above';

      const { choices, correctId } = options(
        [
          { label: `${style} line, shade ${side}`, correct: true },
          { label: `${wrongStyle} line, shade ${side}`, misconceptionId: 'strict-boundary-drawn-solid' },
          { label: `${style} line, shade ${wrongSide}`, misconceptionId: 'half-plane-wrong-side' },
          { label: `${wrongStyle} line, shade ${wrongSide}`, misconceptionId: 'half-plane-wrong-side' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'How should this inequality be graphed?',
        latex: expr(`y ${sign} ${slope === 1 ? '' : slope === -1 ? '-' : slope}x`, signedConstant(intercept)),
        figure: {
          kind: 'coordinate-grid',
          min: -8,
          max: 8,
          lines: [{ slope, intercept, label: 'boundary', dashed: strict }],
        },
        choices,
        correctId,
        summary: `${strict ? 'A strict inequality excludes the line, so it is dashed' : 'Equality is allowed, so the line belongs to the solution and is drawn solid'}, and with y alone on the left the solutions lie ${side} it.`,
        hint: 'Two questions: does the inequality allow equality, and — with y by itself — which side does it name?',
      });
    },
  }),

  template({
    id: 'inequality-two-var-rearranged',
    conceptId: 'two-variable-inequalities',
    description: 'Rearrange for y, where dividing by a negative reverses the sign.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(1, 5);
      const c = rng.int(-8, 8);
      /*
       * Written with y behind a minus sign, so isolating it divides by −1 —
       * the one move in the topic that reverses the inequality, and the reason
       * "greater than" cannot be read as "above" until y stands alone.
       *
       * Both the direction and the strictness vary. A version where the answer
       * is always "below, dashed" would be answerable by a student who never
       * noticed the flip at all, which is the entire thing being tested.
       */
      const greater = rng.bool();
      const strict = rng.bool();
      const leading = a === 1 ? '' : String(a);
      const rhs = expr(`${leading}x`, signedConstant(-c));

      // The original sign, and the reversed one that survives isolating y.
      const original = greater ? (strict ? '>' : '\\ge') : strict ? '<' : '\\le';
      const reversed = greater ? (strict ? '<' : '\\le') : strict ? '>' : '\\ge';
      const plain = greater ? (strict ? '<' : '≤') : strict ? '>' : '≥';
      const side = greater ? 'Below' : 'Above';
      const flipped = greater ? 'Above' : 'Below';
      const style = strict ? 'dashed' : 'solid';
      const wrongStyle = strict ? 'solid' : 'dashed';

      const { choices, correctId } = options(
        [
          { label: `${side} the boundary, which is ${style}`, correct: true },
          { label: `${flipped} the boundary, which is ${style}`, misconceptionId: 'inequality-sign-not-flipped' },
          { label: `${side} the boundary, which is ${wrongStyle}`, misconceptionId: 'strict-boundary-drawn-solid' },
          { label: 'On the boundary only', misconceptionId: 'test-point-on-boundary' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Where do the solutions of this inequality lie?',
        latex: `${leading}x - y ${original} ${c}`,
        choices,
        correctId,
        summary: `Isolating y divides by −1, which reverses the sign: the inequality reads y ${plain} ${rhs}. So the solutions are ${side.toLowerCase()} the boundary — the opposite of what the original sign suggests — and the boundary is ${style} because equality is ${strict ? 'not allowed' : 'allowed'}.`,
        hint: 'Get y on its own first. What happens to the inequality sign on the way?',
        steps: [
          { label: 'Move the y across, and the constant with it.', latex: `${rhs} ${original} y` },
          { label: 'Read it with y on the left — the sign turns round.', latex: `y ${reversed} ${rhs}` },
        ],
      });
    },
  }),

  template({
    id: 'inequality-system-overlap',
    conceptId: 'two-variable-inequalities',
    description: 'Find the point satisfying both inequalities of a system, not just one.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const c1 = rng.int(1, 6);
      const c2 = rng.int(1, 6);
      /*
       * y ≤ x + c₁ and y ≥ −x + c₂ meet at a wedge opening to the right. x is
       * chosen far enough right that the wedge is at least two units tall
       * there — to the left of the crossing point the two half-planes do not
       * overlap at all, and no correct option would exist.
       */
      const x = rng.int(Math.max(1, Math.ceil((c2 - c1) / 2) + 1), 8);
      const low = -x + c2;
      const high = x + c1;
      const both = Math.round((low + high) / 2);
      const firstOnly = low - rng.int(1, 3);
      const secondOnly = high + rng.int(1, 3);

      const { choices, correctId } = options(
        [
          { label: `(${x}, ${both})`, correct: true },
          { label: `(${x}, ${firstOnly})`, misconceptionId: 'system-satisfies-one-equation' },
          { label: `(${x}, ${secondOnly})`, misconceptionId: 'system-satisfies-one-equation' },
          { label: `(${-x}, ${secondOnly})`, misconceptionId: 'half-plane-wrong-side' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which point satisfies both inequalities?',
        latex: `y \\le x ${signedConstant(c1)}, \\quad y \\ge -x ${signedConstant(c2)}`,
        figure: {
          kind: 'coordinate-grid',
          min: -8,
          max: 8,
          lines: [
            { slope: 1, intercept: c1, shade: 'below' },
            { slope: -1, intercept: c2, shade: 'above' },
          ],
        },
        choices,
        correctId,
        summary: `At x = ${x} the two boundaries sit at y = ${high} and y = ${low}, so a solution needs a y between them. (${x}, ${both}) is the only one that satisfies both.`,
        hint: 'Check every point against *both* inequalities. Two of these obey one and break the other.',
      });
    },
  }),
];
